import { useState } from "react";
import type { Intent, TurnNode } from "@/types";
import {
  ALL_INTENTS,
  INTENT_LABELS,
  TERMINAL_ACTIONS,
  TRANSFER_WAIT,
  getTransitionTargets,
} from "@/types";
import { AlertTriangle, ChevronDown, ChevronRight, Plus, Sparkles, Trash2 } from "lucide-react";

function TurnNodeCard({
  node, nodes, index, disabled,
  onChange, onDelete, onToggleEntry,
}: {
  node: TurnNode;
  nodes: TurnNode[];
  index: number;
  disabled: boolean;
  onChange: (updated: TurnNode) => void;
  onDelete: () => void;
  onToggleEntry: () => void;
}) {
  const [expanded, setExpanded] = useState(node.isEntry || index === 0);
  const targets = getTransitionTargets(nodes);

  const updateTransition = (intent: Intent, field: "target" | "speech", value: string) => {
    const transitions = { ...node.transitions };
    const existing = transitions[intent] || { target: "" };
    transitions[intent] = { ...existing, [field]: value };
    if (field === "target" && !value) {
      delete transitions[intent];
    }
    onChange({ ...node, transitions });
  };

  return (
    <div className={`border rounded-lg ${node.isEntry ? "border-blue-300 bg-blue-50/50" : "border-slate-200 bg-white"}`}>
      <div
        className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className="font-mono text-sm text-slate-500">{node.id}</span>
        <span className="font-medium text-slate-900">{node.name}</span>
        {node.isEntry && (
          <span className="px-1.5 py-0.5 bg-blue-500 text-white text-[10px] rounded">開始</span>
        )}
        <span className="ml-auto text-xs text-slate-400">
          {Object.keys(node.transitions).length} 遷移
        </span>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">ノードID</label>
              <input
                type="text"
                value={node.id}
                onChange={(e) => onChange({ ...node, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })}
                disabled={disabled}
                className="w-full border rounded px-2 py-1.5 text-sm font-mono disabled:bg-slate-50"
                placeholder="greeting"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">表示名</label>
              <input
                type="text"
                value={node.name}
                onChange={(e) => onChange({ ...node, name: e.target.value })}
                disabled={disabled}
                className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-50"
                placeholder="挨拶"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">発話テキスト</label>
            <textarea
              value={node.speech}
              onChange={(e) => onChange({ ...node, speech: e.target.value })}
              disabled={disabled}
              rows={2}
              className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-50"
            />
            <p className="text-[10px] text-slate-400 mt-0.5">
              {"{company}, {name}, {product_name}, {product_pitch}"}
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">遷移ルール</label>
            <div className="border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-2 py-1.5 font-medium text-slate-600 w-20">インテント</th>
                    <th className="text-left px-2 py-1.5 font-medium text-slate-600">応答テキスト</th>
                    <th className="text-left px-2 py-1.5 font-medium text-slate-600 w-40">遷移先</th>
                  </tr>
                </thead>
                <tbody>
                  {ALL_INTENTS.map((intent) => {
                    const t = node.transitions[intent];
                    return (
                      <tr key={intent} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 text-slate-700 font-medium">{INTENT_LABELS[intent]}</td>
                        <td className="px-1 py-1">
                          <input
                            type="text"
                            value={t?.speech || ""}
                            onChange={(e) => updateTransition(intent, "speech", e.target.value)}
                            disabled={disabled}
                            className="w-full border rounded px-1.5 py-1 text-xs disabled:bg-slate-50"
                            placeholder="(ノード発話を使用)"
                          />
                        </td>
                        <td className="px-1 py-1">
                          <select
                            value={t?.target || ""}
                            onChange={(e) => updateTransition(intent, "target", e.target.value)}
                            disabled={disabled}
                            className="w-full border rounded px-1.5 py-1 text-xs disabled:bg-slate-50"
                          >
                            <option value="">(同じノードに留まる)</option>
                            {targets.map((target) => (
                              <option key={target.value} value={target.value}>{target.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {!disabled && (
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onToggleEntry}
                className={`text-xs px-2 py-1 rounded border ${
                  node.isEntry
                    ? "bg-blue-100 text-blue-700 border-blue-300"
                    : "bg-white text-slate-600 border-slate-300 hover:border-blue-300"
                }`}
              >
                {node.isEntry ? "開始ノード" : "開始に設定"}
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 size={12} className="inline mr-1" />
                削除
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CustomFlowEditor({
  isDraft,
  companyName,
  productName,
  productPitch,
  turnNodes,
  turnsErrors,
  aiPrompt,
  aiGenerating,
  aiPreview,
  onCompanyNameChange,
  onProductNameChange,
  onProductPitchChange,
  onAiPromptChange,
  onUpdateNode,
  onDeleteNode,
  onSetEntryNode,
  onAddNode,
  onLoadTemplate,
  onGenerateWithAi,
  onApplyAiPreviewAll,
  onApplyAiNode,
  onDiscardAiPreview,
}: {
  isDraft: boolean;
  companyName: string;
  productName: string;
  productPitch: string;
  turnNodes: TurnNode[];
  turnsErrors: string[];
  aiPrompt: string;
  aiGenerating: boolean;
  aiPreview: TurnNode[] | null;
  onCompanyNameChange: (value: string) => void;
  onProductNameChange: (value: string) => void;
  onProductPitchChange: (value: string) => void;
  onAiPromptChange: (value: string) => void;
  onUpdateNode: (index: number, node: TurnNode) => void;
  onDeleteNode: (index: number) => void;
  onSetEntryNode: (index: number) => void;
  onAddNode: () => void;
  onLoadTemplate: () => void;
  onGenerateWithAi: () => void;
  onApplyAiPreviewAll: () => void;
  onApplyAiNode: (node: TurnNode) => void;
  onDiscardAiPreview: () => void;
}) {
  return (
    <>
      <p className="text-xs text-slate-500">
        ノードごとに発話と遷移ルールを設定します。未定義のインテントは同じノードに留まります。
      </p>

      <div className="p-3 bg-slate-50 rounded-lg space-y-3">
        <label className="block text-xs font-semibold text-slate-700">差し込み変数</label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] text-slate-500 mb-0.5">{"{company}"} — 自社名</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => onCompanyNameChange(e.target.value)}
              disabled={!isDraft}
              className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-white"
            />
          </div>
          <div>
            <label className="block text-[10px] text-slate-500 mb-0.5">{"{product_name}"} — 商品名</label>
            <input
              type="text"
              value={productName}
              onChange={(e) => onProductNameChange(e.target.value)}
              disabled={!isDraft}
              className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-white"
            />
          </div>
          <div>
            <label className="block text-[10px] text-slate-500 mb-0.5">{"{product_pitch}"} — 商品説明</label>
            <input
              type="text"
              value={productPitch}
              onChange={(e) => onProductPitchChange(e.target.value)}
              disabled={!isDraft}
              className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-white"
            />
          </div>
        </div>
        <p className="text-[10px] text-slate-400">
          {"{name}"} は架電先の担当者名に自動置換されます
        </p>
      </div>

      {turnsErrors.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-1 text-amber-700 text-xs font-medium mb-1">
            <AlertTriangle size={14} /> バリデーションエラー
          </div>
          <ul className="text-xs text-amber-700 space-y-0.5">
            {turnsErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        {turnNodes.map((node, i) => (
          <TurnNodeCard
            key={`${node.id}-${i}`}
            node={node}
            nodes={turnNodes}
            index={i}
            disabled={!isDraft}
            onChange={(updated) => onUpdateNode(i, updated)}
            onDelete={() => onDeleteNode(i)}
            onToggleEntry={() => onSetEntryNode(i)}
          />
        ))}
      </div>

      {isDraft && (
        <div className="space-y-3">
          <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-2">
            <label className="flex items-center gap-1 text-xs font-semibold text-violet-700">
              <Sparkles size={14} /> AIでフローを生成
            </label>
            <textarea
              value={aiPrompt}
              onChange={(e) => onAiPromptChange(e.target.value)}
              rows={2}
              className="w-full border border-violet-200 rounded px-2 py-1.5 text-sm placeholder:text-violet-300"
              placeholder="例: LEDバルブの新製品案内。取次を依頼して、興味があればCB予約。忙しければ改めて連絡。"
            />
            <button
              type="button"
              onClick={onGenerateWithAi}
              disabled={!aiPrompt.trim() || aiGenerating}
              className="flex items-center gap-1 text-xs px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors"
            >
              <Sparkles size={14} />
              {aiGenerating ? "生成中..." : "生成する"}
            </button>
          </div>

          {aiPreview && (
            <div className="border-2 border-violet-300 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-violet-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-violet-700">
                  <Sparkles size={12} className="inline mr-1" />
                  AI生成プレビュー（{aiPreview.length}ノード）
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onDiscardAiPreview}
                    className="text-xs px-2 py-1 border border-violet-300 rounded text-violet-600 hover:bg-violet-50"
                  >
                    破棄
                  </button>
                  <button
                    type="button"
                    onClick={onApplyAiPreviewAll}
                    className="text-xs px-3 py-1 bg-violet-600 text-white rounded hover:bg-violet-700"
                  >
                    全部適用
                  </button>
                </div>
              </div>
              <div className="p-3 space-y-2 bg-violet-50/50 max-h-96 overflow-y-auto">
                {aiPreview.map((node) => {
                  const existing = turnNodes.find((n) => n.id === node.id);
                  return (
                    <div key={node.id} className="bg-white border border-violet-200 rounded p-3 text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <span className="font-mono text-violet-600">{node.id}</span>
                          <span className="mx-2 text-slate-400">•</span>
                          <span className="font-medium">{node.name}</span>
                          {existing && (
                            <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px]">
                              上書き
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => onApplyAiNode(node)}
                          className="text-xs px-2 py-1 bg-violet-100 text-violet-700 rounded hover:bg-violet-200"
                        >
                          適用
                        </button>
                      </div>
                      <p className="text-slate-600 mb-2 whitespace-pre-wrap">{node.speech}</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(node.transitions).map(([intent, t]) => (
                          <span key={intent} className="px-2 py-0.5 bg-slate-100 rounded text-[10px] text-slate-600">
                            {INTENT_LABELS[intent as Intent]} → {
                              TERMINAL_ACTIONS.includes(t.target as any) || t.target === TRANSFER_WAIT
                                ? t.target
                                : aiPreview.find((n) => n.id === t.target)?.name || t.target
                            }
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            {turnNodes.length < 10 && (
              <button
                type="button"
                onClick={onAddNode}
                className="flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50"
              >
                <Plus size={14} /> ノードを追加
              </button>
            )}
            {turnNodes.length === 0 && (
              <button
                type="button"
                onClick={onLoadTemplate}
                className="text-xs px-3 py-1.5 border border-blue-300 rounded-lg text-blue-600 hover:bg-blue-50"
              >
                テンプレートを読み込む
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
